// src/pages/RequestsDetail.tsx
import React from 'react'
import { useParams, Link, useSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { atlas, enabledStacks } from '../../api'
import AttachCardSheet from '../../components/AttachCardSheet'
import { auth } from '../../firebase'
import Spinner from '../../components/Spinner'
import { ProfessionKey, PROFESSIONS, resolveProfessionParam, matchPath } from '../../professions'
import { detectTimeZone } from '../../countryRules'

const ENABLED_PROFESSIONS = (enabledStacks.length
  ? (enabledStacks as ProfessionKey[])
  : ['doctor']) as ProfessionKey[]

type Candidate = {
  specialistId: string
  status: 'PENDING' | 'ACCEPTED' | 'CONFIRMED' | 'DECLINED' | 'LOST' | 'CANCELLED' | 'COMPLETED'
  slotISO: string
  feeCents?: number | null
  decisionAt?: string | null
  providerName?: string | null
  providerDisplayName?: string | null
  providerPractice?: string | null
  specialistName?: string | null
}

type Selection = {
  selectionId: string
  createdAt: string
  specialty: string
  reason?: string
  booking?: {
    specialistId: string
    slotISO: string
    feeCents: number
    paymentId?: string
    createdAt: string
    status?: 'CANCELLED' | 'CONFIRMED' | 'COMPLETED'
  } | null
  payment?: {
    bookingFeeCents?: number
    paymentMethodId?: string
    stripeCustomerId?: string
    paymentId?: string
    status?: string
  } | null
  candidates: Candidate[]
  profession: ProfessionKey
  bookingProvider?: {
    userId?: string
    name?: string
    practiceName?: string
    publicContact?: {
      name?: string
      email?: string
      phone?: string
      address?: { state?: string; postalCode?: string | number }
    }
  } | null
}

const CONTACT_SLOT = 'REQUEST_BOOKING'

function isPaid(sel?: Selection | null) {
  const payment = sel?.payment
  const status = String(payment?.status || '').toLowerCase()
  if (status === 'waived') return true
  if (Number(payment?.bookingFeeCents) === 0) return true
  return status === 'succeeded' || status === 'processing' || status === 'requires_capture'
}

function money(cents?: number | null) {
  if (cents == null) return '—'
  return `$${(cents / 100).toFixed(2)}`
}

const slotExpired = (iso?: string) => {
  if (iso === CONTACT_SLOT) return false
  if (!iso) return false
  const ts = Date.parse(iso)
  if (!Number.isFinite(ts)) return false
  return ts <= Date.now()
}

const fmtSlot = (iso?: string, timeZone: string = detectTimeZone()) => {
  if (!iso) return ''
  if (iso === CONTACT_SLOT) return 'No time selected — contact the client to arrange'
  try {
    return new Date(iso).toLocaleString('en-AU', { timeZone, hour12: false })
  } catch {
    return ''
  }
}

export default function RequestsDetailPage() {
  const nav = useNavigate()
  const displayTimeZone = React.useMemo(() => detectTimeZone(), [])
  // Accept selectionId from param, query, or navigation state
  const { selectionId: idFromParams } = useParams<{ selectionId: string }>()
  const [sp] = useSearchParams()
  const location = useLocation()

  const selectionId =
    idFromParams ||
    sp.get('selectionId') ||
    sp.get('id') ||
    (location.state as any)?.selectionId ||
    ''
  const stackParam = sp.get('stack')
  const viewParam = (sp.get('view') || 'client').toLowerCase()
  const isProviderView = viewParam === 'provider'
  const preferredProfession = stackParam ? resolveProfessionParam(stackParam) : null
  const professionOrder = React.useMemo(() => {
    const keys = ENABLED_PROFESSIONS
    if (preferredProfession && keys.includes(preferredProfession)) {
      return [preferredProfession, ...keys.filter(k => k !== preferredProfession)]
    }
    return keys
  }, [preferredProfession])

  // 👇 NEW: know if we just came from the payment flow
  const cameFromPayment = (location.state as any)?.from === 'payment'

  const [data, setData] = React.useState<Selection | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [err, setErr] = React.useState<string | null>(null)

  // Card sheet
  const [cardOpen, setCardOpen] = React.useState(false)
  const [fundCardOpen, setFundCardOpen] = React.useState(false)
  const [fundSpecialistId, setFundSpecialistId] = React.useState<string | null>(null)
  const emailHint = React.useMemo(() => auth.currentUser?.email ?? '', [])
  const [cancelBusy, setCancelBusy] = React.useState(false)
  const [actionBusy, setActionBusy] = React.useState(false)
  const providerLabelSingular = data?.profession === 'doctor' ? 'Specialist' : 'Provider'
  const providerLabelPlural = providerLabelSingular === 'Specialist' ? 'Specialists' : 'Providers'
  const providerPluralLower = providerLabelPlural.toLowerCase()

  // Retry-aware loader to smooth out eventual consistency just after payment
  const load = React.useCallback(async () => {
    if (!selectionId) return
    setLoading(true)
    setErr(null)

    const sleep = (ms: number) => new Promise(res => setTimeout(res, ms))

    // 👇 NEW: tiny initial wait only if we just navigated here from payment
    if (cameFromPayment) {
      await sleep(300) // 300–500ms is fine
    }

    const tryStacks = async () => {
      for (const prof of professionOrder) {
        try {
          const res = await atlas.getSelection(prof, selectionId)
          return { selection: res, profession: prof }
        } catch (err: any) {
          const msg = String(err?.message || '')
          if (/404|Not\s*Found/i.test(msg) || msg.toLowerCase().includes('forbidden')) {
            continue
          }
          throw err
        }
      }
      const error = new Error('Not found')
      ;(error as any).statusCode = 404
      throw error
    }

    for (let attempt = 0; attempt < 5; attempt++) {
      try {
        const { selection, profession } = await tryStacks()
        setData({ ...selection, profession })
        setLoading(false)
        return
      } catch (e: any) {
        const msg = String(e?.message || '')
        // Only retry 404-ish errors; surface others immediately
        if (!/404|Not\s*Found/i.test(msg)) {
          setErr(e?.message || 'Failed to load request')
          setLoading(false)
          return
        }
        await sleep(200 * Math.pow(2, attempt)) // 200, 400, 800, 1600, 3200
      }
    }

    setErr('Still preparing your request. Please reload in a moment.')
    setLoading(false)
  }, [selectionId, cameFromPayment]) // 👈 UPDATED deps

  React.useEffect(() => { load() }, [load])

  if (!selectionId) {
    return (
      <div className="container">
        <div className="banner error">Missing selectionId</div>
      </div>
    )
  }

  if (loading) return <div className="container page-wide"><Spinner label="Loading details…" /></div>
  if (err) return <div className="container page-wide"><div className="banner error">{err}</div></div>
  if (!data) return <div className="container page-wide"><p>Not found.</p></div>

  const paid = isPaid(data)
  const filled = Boolean(data.booking)
  const hasPending = data.candidates.some(c => c.status === 'PENDING')
  const hasAccepted = data.candidates.some(c => c.status === 'ACCEPTED' || c.status === 'CONFIRMED')
  const hasFuturePending = data.candidates.some(
    c => c.status === 'PENDING' && !slotExpired(c.slotISO)
  )
  const requestExpired = paid && !filled && hasPending && !hasFuturePending
  const acceptedCandidate = data.booking
    ? data.candidates.find(c => c.specialistId === data.booking?.specialistId) || null
    : null
  const reviewable = filled && data.booking && (data.booking.slotISO === CONTACT_SLOT || slotExpired(data.booking.slotISO))
  const reviewHref = isProviderView
    ? `/dashboard/${encodeURIComponent(data.selectionId)}?stack=${data.profession}`
    : `/appointments/${encodeURIComponent(data.selectionId)}?view=client&stack=${data.profession}&review=1`
  const reviewCopy = isProviderView
    ? 'Please rate the client after the booking is completed.'
    : 'Review your provider in Appointments after your consult.'
  const reviewCtaLabel = isProviderView ? 'Open dashboard' : 'Leave review'

  return (
    <div className="container page-wide request-detail-page">
      <div className="page-head">
        <div>
          <p className="hero-kicker">Clients · {PROFESSIONS[data.profession].label}</p>
          <h1>Request detail</h1>
          <p className="hint">Manage your request and responses here.</p>
        </div>
        <div className="inline-gap-2">
          {!filled && (
            <button
              className="btn btn-outline"
              disabled={cancelBusy}
              onClick={async () => {
                if (!confirm('Cancel this request?')) return
                setCancelBusy(true)
                try {
                  await atlas.cancelSelection(data.profession, data.selectionId)
                  nav(`/requests?stack=${data.profession}`)
                } catch (e:any) {
                  alert(e?.message || 'Failed to cancel request')
                } finally {
                  setCancelBusy(false)
                }
              }}
            >
              {cancelBusy ? 'Cancelling…' : 'Cancel request'}
            </button>
          )}
          {!filled && !paid && (
            <button
              className="btn btn-primary"
              onClick={() => setCardOpen(true)}
              title={`Submit your request so ${providerPluralLower} are notified (no payment needed)`}
            >
              Submit request
            </button>
          )}
          <Link className="btn btn-outline" to={`/requests?stack=${data.profession}`}>Back</Link>
        </div>
      </div>

      {reviewable && (
        <section className="card section-card" style={{ margin: '12px 0', textAlign: 'center' }}>
          <div className="inline-gap-2" style={{ alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <span>{reviewCopy}</span>
            <Link className="btn btn-primary btn-small" to={reviewHref}>
              {reviewCtaLabel}
            </Link>
          </div>
        </section>
      )}

      <section className="card section-card">
        <header>
          <div>
            <h3>{data.specialty}</h3>
            <p className="hint">{new Date(data.createdAt).toLocaleString('en-AU', { timeZone: displayTimeZone, hour12: false })}</p>
          </div>
          <span className={`status-pill ${
            filled ? 'status-accepted'
              : requestExpired ? 'status-lost'
              : paid ? 'status-pending'
              : 'status-lost'
          }`}>
            {filled ? 'Filled' : !paid ? 'Pending submission' : requestExpired ? 'Expired' : 'Open'}
          </span>
        </header>
        <div className="list">
          {data.reason && (
            <div style={{ whiteSpace: 'pre-wrap' }}>
              <b>Brief:</b> {data.reason}
            </div>
          )}
          {data.booking && (
            <div>
              <b>Selected slot:</b>{' '}
              {fmtSlot(data.booking.slotISO, displayTimeZone)}
            </div>
          )}
        </div>
        {requestExpired && (
          <div className="banner warning stack-2" style={{ marginTop: 12 }}>
            <p style={{ margin: 0 }}>
              All requested times have passed. Please submit a new request with updated availability so {providerPluralLower} can respond.
            </p>
            <div>
              <Link className="btn btn-small" to={matchPath(data.profession)}>
                Start a new request
              </Link>
            </div>
          </div>
        )}
      </section>
      {filled && data.booking && (
        <section className="card section-card">
          <header>
            <div>
              <h3>Confirmed booking</h3>
            </div>
          </header>
          <div className="list">
            <div>
              <b>When ({displayTimeZone}):</b>{' '}
              {fmtSlot(data.booking.slotISO, displayTimeZone)}
            </div>
            <div>
              <b>{providerLabelSingular}:</b>{' '}
              {acceptedCandidate?.providerDisplayName ||
                acceptedCandidate?.providerName ||
                acceptedCandidate?.specialistName ||
                '—'}
            </div>
            {!!data.booking.status && <div><b>Status:</b> {data.booking.status}</div>}
            <div>
              <b>Payment:</b> {String(data.payment?.status || 'pending')}
            </div>
          </div>
          {!isProviderView && data.booking.status !== 'COMPLETED' && (
            <div className="inline-gap-2" style={{ marginTop: 12 }}>
              <button
                className="btn btn-primary"
                disabled={actionBusy}
                onClick={async () => {
                  setActionBusy(true)
                  try {
                    await atlas.confirmSelectionComplete(data.profession, data.selectionId)
                    await load()
                  } catch (e:any) {
                    alert(e?.message || 'Failed to confirm completion')
                  } finally {
                    setActionBusy(false)
                  }
                }}
              >
                {actionBusy ? 'Confirming…' : 'Confirm job completed'}
              </button>
            </div>
          )}
        </section>
      )}

      <section className="card section-card">
        <header>
          <div>
            <h3>{providerLabelPlural} responses</h3>
            <p className="hint">{data.candidates.length || 0} responses</p>
          </div>
        </header>
        <div className="list">
          {data.candidates.length === 0 && <div className="minor">No candidates yet.</div>}
          {data.candidates.map(c => {
            const providerDisplay =
              c.providerDisplayName || c.providerName || c.specialistName || 'Provider'
            const titleParts = [
              c.providerName || c.specialistName || null,
              c.providerPractice || null,
            ].filter(Boolean)
            const providerTitle = titleParts.length ? titleParts.join(' • ') : c.specialistId
            return (
              <div key={c.specialistId} className="inline-gap-2" style={{ alignItems: 'center', flexWrap:'wrap' }}>
                <span className="pill" title={providerTitle}>{providerDisplay}</span>
                <span className={`status-pill ${
                  c.status === 'ACCEPTED' ? 'status-accepted' :
                  c.status === 'DECLINED' ? 'status-declined' :
                  c.status === 'LOST'     ? 'status-lost' :
                  c.status === 'CANCELLED'? 'status-cancelled' :
                  (c.status === 'PENDING' && slotExpired(c.slotISO)) ? 'status-lost' :
                  'status-pending'
                }`}>
                  {c.status === 'PENDING' && slotExpired(c.slotISO) ? 'Expired' : c.status}
                </span>
                <span className="text-sm text-gray-600">
                  {fmtSlot(c.slotISO, displayTimeZone)}
                </span>
                {c.feeCents != null && <span className="pill">{money(c.feeCents)}</span>}
                {!isProviderView && !filled && hasAccepted && (c.status === 'ACCEPTED' || c.status === 'CONFIRMED') && (
                  <button
                    className="btn btn-primary btn-small"
                    disabled={actionBusy}
                    onClick={() => {
                      setFundSpecialistId(c.specialistId)
                      setFundCardOpen(true)
                    }}
                  >
                    Select & hold payment
                  </button>
                )}
                {c.status === 'PENDING' && slotExpired(c.slotISO) && (
                  <span className="hint">Requested time passed</span>
                )}
              </div>
            )
          })}
        </div>
      </section>

      {/* Pay booking fee / add card sheet */}
      <AttachCardSheet
        open={cardOpen}
        onClose={() => setCardOpen(false)}
        selectionId={data.selectionId}
        profession={data.profession}
        emailHint={emailHint}
        onAttached={() => {
          setCardOpen(false)
          load() // refresh to flip status to OPEN
        }}
      />
      <AttachCardSheet
        open={fundCardOpen}
        onClose={() => {
          if (actionBusy) return
          setFundCardOpen(false)
          setFundSpecialistId(null)
        }}
        selectionId={data.selectionId}
        profession={data.profession}
        emailHint={emailHint}
        onPaymentMethod={async (paymentMethodId) => {
          if (!fundSpecialistId) throw new Error('Missing selected professional')
          setActionBusy(true)
          try {
            await atlas.fundSelection(data.profession, data.selectionId, {
              specialistId: fundSpecialistId,
              paymentMethodId,
            })
            setFundCardOpen(false)
            setFundSpecialistId(null)
            await load()
          } finally {
            setActionBusy(false)
          }
        }}
      />
    </div>
  )
}
